import copyText from "@/constants/copyText";
import { parseFullyQualifiedResourceString } from "@/features/admin/integrations/components/FullyQualifiedResourceString";
import { FormField } from "@/ui-lib/components/Form";
import Select from "@/ui-lib/components/Select";
import TextInput from "@/ui-lib/components/TextInput";
import { useTheme } from "@emotion/react";
import Box from "@ternary/web-ui-lib/components/Box";
import React, { useMemo } from "react";

export interface BigQueryConfig {
  projectID: string | null;
  datasetID: string | null;
  tableID: string | null;
}

interface Props {
  error?: {
    projectID?: boolean;
    datasetID?: boolean;
    tableID?: boolean;
  };
  labels?: {
    projectID?: string;
    datasetID?: string;
    tableID?: string;
  };
  bigQueryDatasets?: { id: string }[];
  bigQueryTables?: { id: string }[];
  value: BigQueryConfig;
  isLoadingDatasets?: boolean;
  isLoadingTables?: boolean;
  onChange: (
    fieldName: keyof BigQueryConfig,
    fieldValue: string | null
  ) => void;
}

export default function BigQueryInputField({
  error,
  labels,
  value,
  bigQueryDatasets = [],
  bigQueryTables = [],
  isLoadingDatasets = false,
  isLoadingTables = false,
  onChange,
}: Props): JSX.Element {
  const theme = useTheme();

  const datasetOptions = useMemo(
    () =>
      bigQueryDatasets.map((dataset) => ({
        label: dataset.id,
        value: dataset.id,
      })),
    [bigQueryDatasets]
  );

  const tableOptions = useMemo(
    () =>
      bigQueryTables.map((table) => ({
        label: table.id,
        value: table.id,
      })),
    [bigQueryTables]
  );

  const handleChange = (
    fieldName: keyof BigQueryConfig,
    fieldValue: string | null
  ) => {
    onChange(fieldName, fieldValue);
  };

  // Parses BigQuery fully qualified table ID from clipboard in format: PROJECT_ID.DATASET_ID.TABLE_ID
  // Example: ternary-dev.data_jtMgZDdsadsad7F43qDSAAKwVWK.table_id
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text");
    try {
      e.preventDefault();
      const resourceParts = parseFullyQualifiedResourceString(pastedText);
      const [projectID, datasetID, tableID] = resourceParts.parts;
      onChange("projectID", projectID);
      onChange("datasetID", datasetID);
      onChange("tableID", tableID);
    } catch {
      // Do nothing - let the paste event continue normally
    }
  };

  return (
    <Box marginBottom={theme.space_sm}>
      <FormField
        errorCaption={
          error?.projectID ? copyText.bigQueryProjectIDError : undefined
        }
        caption={copyText.bigQueryProjectIDInputCaption}
        input={TextInput}
        label={labels?.projectID ?? copyText.bigQueryProjectID}
        name="projectID"
        required
        value={value.projectID ?? undefined}
        variant={error?.projectID ? "danger" : undefined}
        onChange={(e) => handleChange("projectID", e.target.value)}
        onPaste={handlePaste}
      />

      <FormField
        errorCaption={
          error?.datasetID ? copyText.bigQueryDatasetIDError : undefined
        }
        label={labels?.datasetID ?? copyText.bigQueryDatasetID}
      >
        <Select
          disabled={!value.projectID || isLoadingDatasets}
          isCreatable
          isLoading={isLoadingDatasets}
          menuPlacement="bottom"
          options={datasetOptions}
          required
          searchable
          value={
            value.datasetID
              ? [{ label: value.datasetID, value: value.datasetID }]
              : []
          }
          variant={error?.datasetID ? "danger" : undefined}
          onChange={(option) =>
            handleChange("datasetID", option?.value ?? null)
          }
        />
      </FormField>

      <FormField
        errorCaption={
          error?.tableID ? copyText.bigQueryTableIDError : undefined
        }
        label={labels?.tableID ?? copyText.bigQueryTableID}
      >
        <Select
          disabled={!value.datasetID || isLoadingTables}
          isCreatable
          isLoading={isLoadingTables}
          menuPlacement="bottom"
          options={tableOptions}
          required
          searchable
          value={
            value.tableID
              ? [{ label: value.tableID, value: value.tableID }]
              : []
          }
          variant={error?.tableID ? "danger" : undefined}
          onChange={(option) => handleChange("tableID", option?.value ?? null)}
        />
      </FormField>
    </Box>
  );
}
